import type { Action } from 'typescript-fsa';
import type { AppState } from '../manager';
import type { AssessmentSliderEvent } from './types';

export type CaptureRequest = { capture_request_id: number };

export type CaptureSync = {
  scope: string;
  revision: number;
  snapshot?: { id: number; revision: number; hadPendingWrites: boolean };
  pending: Record<number, { scenario?: string; periodId?: number | null }>;
  sliders?: Record<
    string,
    {
      baseline?: number;
      confirmed?: AssessmentSliderEvent;
      pending: number[];
    }
  >;
};

let requestSequence = 0;

// Each request keeps its own identity through the existing API manager. The
// manager runs requests concurrently; response order need not match input order.
export const withCaptureRequest =
  <Payload extends CaptureRequest>(
    create: (payload: Payload) => Action<Payload>,
  ) =>
  (payload: Omit<Payload, keyof CaptureRequest>) => {
    const id = ++requestSequence;
    return create({
      ...payload,
      capture_request_id: id,
      // Both assessment surfaces used to allocate overlapping negative ids.
      ...('temp_id' in payload ? { temp_id: -id } : {}),
    } as Payload);
  };

export const assessmentCaptureScope = (
  sessionId: number,
  playerId: number | undefined,
  elapsedDays: number | undefined,
) => `${sessionId}:${playerId ?? 'observer'}:${elapsedDays ?? 'unknown'}`;

const scopeForState = (state: AppState) =>
  assessmentCaptureScope(
    state.active_session,
    state.player.item?.id,
    state.sessions.items[state.active_session]?.elapsed_days,
  );

const syncForState = (state: AppState): CaptureSync => {
  const scope = scopeForState(state);
  if (state.assessment.capture_sync?.scope !== scope) {
    state.assessment.capture_sync = { scope, revision: 0, pending: {} };
  }
  return state.assessment.capture_sync;
};

export const startCaptureSnapshot = (
  state: AppState,
  request: CaptureRequest,
) => {
  const sync = syncForState(state);
  sync.snapshot = {
    id: request.capture_request_id,
    revision: sync.revision,
    hadPendingWrites: Object.keys(sync.pending).length > 0,
  };
};

export const currentCaptureSnapshot = (
  state: AppState,
  request: CaptureRequest,
) => {
  const sync = state.assessment.capture_sync;
  return sync?.scope === scopeForState(state) &&
    sync.snapshot?.id === request.capture_request_id
    ? sync
    : undefined;
};

export const snapshotOverlapsCapture = (sync: CaptureSync) =>
  sync.snapshot?.hadPendingWrites || sync.snapshot?.revision !== sync.revision;

export const startCaptureWrite = (state: AppState, request: CaptureRequest) => {
  const sync = syncForState(state);
  sync.revision += 1;
  sync.pending[request.capture_request_id] = {
    scenario: state.assessment.scenario,
    periodId: state.assessment.current_period_id,
  };
};

export const finishCaptureWrite = (
  state: AppState,
  request: CaptureRequest,
  resultPeriodId?: number,
) => {
  const sync = state.assessment.capture_sync;
  const pending = sync?.pending[request.capture_request_id];
  if (!sync || !pending) return false;
  delete sync.pending[request.capture_request_id];
  sync.revision += 1;
  const currentPeriodId = state.assessment.current_period_id;
  return (
    sync.scope === scopeForState(state) &&
    pending.scenario === state.assessment.scenario &&
    (currentPeriodId == null ||
      (resultPeriodId ?? pending.periodId) === currentPeriodId)
  );
};

export const startCaptureSlider = (
  state: AppState,
  request: CaptureRequest,
  code: string,
) => {
  startCaptureWrite(state, request);
  const sync = state.assessment.capture_sync!;
  sync.sliders ??= {};
  sync.sliders[code] ??= {
    baseline: state.assessment.sliders[code],
    pending: [],
  };
  sync.sliders[code].pending.push(request.capture_request_id);
};

const compareSliderEvents = (
  left: AssessmentSliderEvent,
  right: AssessmentSliderEvent,
) => {
  // Django timestamps include microseconds; Date.parse alone truncates them.
  const subMilliseconds = (timestamp: string) =>
    Number((timestamp.match(/\.(\d+)/)?.[1] || '').padEnd(6, '0').slice(3, 6));
  return (
    (Date.parse(left.created_at) || 0) - (Date.parse(right.created_at) || 0) ||
    subMilliseconds(left.created_at) - subMilliseconds(right.created_at) ||
    left.id - right.id
  );
};

export const finishCaptureSlider = (
  state: AppState,
  request: CaptureRequest,
  code: string,
  result?: AssessmentSliderEvent,
) => {
  if (!finishCaptureWrite(state, request, result?.period)) return false;
  const updates = state.assessment.capture_sync?.sliders;
  const update = updates?.[code];
  if (!update || !updates) return false;
  update.pending = update.pending.filter(
    id => id !== request.capture_request_id,
  );
  const confirmed = update.confirmed;
  if (result && (!confirmed || compareSliderEvents(result, confirmed) > 0)) {
    update.confirmed = result;
  }
  // Retain the latest optimistic input until overlapping saves settle, then
  // show the latest persisted event, not whichever HTTP response arrived last.
  if (!update.pending.length) {
    const value = update.confirmed?.value ?? update.baseline;
    if (value === undefined) delete state.assessment.sliders[code];
    else state.assessment.sliders[code] = value;
    delete updates[code];
  }
  return true;
};
